import { BadgeVariant } from "shared/components";
import { DateTime } from "luxon";
import type { OwnerReportSettings } from "api/owner-report-settings";
import type { OwnerReportShare } from "api/owner-report-shares";

/**
 * What the three "What owners will see" tiles count. Derived from the partner's
 * settings rather than asked of the API: the dialog already has them, and the
 * numbers are just how many switches are on.
 */
export const reportScope = (settings: OwnerReportSettings) => ({
  totals: Object.entries(settings.totals).filter(([key, on]) => {
    if (!on) return false;
    // Not a total of its own — it says whose figures the others are.
    if (key === "include_guarantors_in_totals") return false;
    // Drawn from a report this sender's role cannot open, so the API drops it.
    if (settings.restricted_metrics.includes(key.replace("show_", "")))
      return false;
    // Desired lease term is only offered to partners using Yardi lease terms, so
    // a stale `true` on a partner without them is not something an owner sees.
    return key !== "show_desired_lease_term" || settings.lease_term_available;
  }).length,
  people: Object.values(settings.people).filter(Boolean).length,
  sections: Object.entries(settings.sections).filter(
    ([key, on]) =>
      on && !settings.restricted_sections.includes(key.replace("section_", ""))
  ).length
});

const shortDate = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("MMM d") : null;

const views = (count: number) => {
  if (count === 0) return "No views";
  return count === 1 ? "1 view" : `${count} views`;
};

/**
 * The line under a shared link's addresses. Its first part is whichever date the
 * link's state makes relevant — when it dies, when it was withdrawn, or when it
 * lapsed — so a partner reads the state and its date together.
 */
export const shareMetaLine = (share: OwnerReportShare) => {
  const lifespan = {
    active: `Expires ${shortDate(share.expires_at)}`,
    revoked: `Revoked ${shortDate(share.revoked_at)}`,
    expired: `Expired ${shortDate(share.expires_at)}`
  }[share.state];

  const sender = share.shared_by?.name;
  const by = sender ? ` by ${sender}` : "";

  return [
    lifespan,
    `Sent ${shortDate(share.shared_at)}${by}`,
    views(share.view_count)
  ]
    .filter(Boolean)
    .join(" · ");
};

export const STATE_LABELS: Record<OwnerReportShare["state"], string> = {
  active: "Active",
  revoked: "Revoked",
  expired: "Expired"
};

/** The design system's own badge tones, so these match status pills elsewhere. */
export const STATE_VARIANTS: Record<OwnerReportShare["state"], BadgeVariant> = {
  active: BadgeVariant.SUCCESS,
  revoked: BadgeVariant.BASE,
  expired: BadgeVariant.BASE
};
